import React from 'react';

import DromoUploader from 'dromo-uploader-react';

import { convertUserToDromoUser } from './helpers';
import { post } from 'helpers/api';

import type * as DromoTypes from 'dromo-uploader-react';
import type { DromoImporterConfig, DromoImporterProps, Import } from './types';

type Props = DromoImporterConfig & DromoImporterProps & {
  onCreate: (dromoImport: Import) => void;
  onError: () => void;
  onHide: () => void;
}

export default function DromoImporter({
  apiKey,
  bulkRowHooks,
  columnHooks,
  description,
  developmentMode,
  dromoSchemaId,
  fields,
  forUser,
  groupId,
  importerOptions,
  importerType,
  rowDeleteHooks,
  rowHooks,
  stepHooks,
  title,
  version,
  onCreate,
  onError,
  onHide,
}: Props) {
  async function finish(data, { id: dromoId }) {
    try {
      const dromoImport = await post('/dromo/imports', {
        data,
        description,
        dromoId,
        dromoSchemaId,
        groupId,
        importerOptions,
        importerType,
      });
      onCreate(dromoImport);
    } catch (error) {
      onError();
    } finally {
      onHide();
    }
  }

  let dromoProps;
  if (fields) {
    let importIdentifier = groupId;
    if (version) {
      importIdentifier += `_v${version}`;
    }

    const settings: DromoTypes.IDeveloperSettings = {
      autoMapHeaders: true,
      backendSyncMode: 'FULL_DATA',
      developmentMode,
      importIdentifier,
      reviewStep: {
        // AI transformation feature
        // If using a saved schema instead of fields, this must enabled in the saved schema's JSON
        enableUserTransformations: true,
      },
      title: `Add ${title}`,
    };

    dromoProps = {
      fields: fields,
      settings,
    };
  } else {
    dromoProps = {
      // When using a saved schema, the settings prop is completely ignored (even if set)
      developmentMode: true,
      schemaId: dromoSchemaId,
    };
  }

  return (
    <DromoUploader
      bulkRowHooks={bulkRowHooks}
      columnHooks={columnHooks}
      licenseKey={apiKey}
      open
      rowDeleteHooks={rowDeleteHooks}
      rowHooks={rowHooks}
      stepHooks={stepHooks}
      user={convertUserToDromoUser(forUser)}
      onCancel={onHide}
      onResults={finish}
      {...dromoProps}
    />
  );
}
